import React, { useState } from "react";
import { createPortal } from "react-dom";
import Close from "@mui/icons-material/Close";
import {
  AddOriginSearchOption,
  IFavoriteLocationExtension,
  IFavoriteOriginOptionExtension,
  OriginTypeFilter,
} from "../../../types/IFavoriteOrigins";
import { MONTHS_LOCAL } from "../../../types/IMonth";
import { PendingAddProductPrompt } from "../../../types/IOriginManager";
import NitroService from "../../../utils/nitro.service";
import { IOccupant } from "../../../types/ILocation";

interface UseAddOriginProductManagerParams {
  product: string;
  selectedMonths: string[];
  currentMonth: number;
  newlyAddedOrigins: Record<string, IFavoriteLocationExtension[]>;
  onOriginRowsAdded?: (
    rows: IFavoriteLocationExtension[],
  ) => Promise<void> | void;
  setDeletedRowKeysByProduct: React.Dispatch<
    React.SetStateAction<Record<string, Set<string>>>
  >;
  setNewlyAddedOriginsByProduct: React.Dispatch<
    React.SetStateAction<Record<string, IFavoriteLocationExtension[]>>
  >;
  setEditingNewlyAddedIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  setJustAddedByProduct: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  setAddOriginQuery: React.Dispatch<React.SetStateAction<string>>;
  setOriginTypeFilter: React.Dispatch<React.SetStateAction<OriginTypeFilter>>;
  setIsAddOriginOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setFavoriteOriginOptions: React.Dispatch<
    React.SetStateAction<IFavoriteOriginOptionExtension[]>
  >;
  savedNotification: (message: string) => void;
  failedNotification: (title: string, message: string) => void;
}

interface AddProductPromptModalProps {
  pendingAddProductPrompt: PendingAddProductPrompt | null;
  isAddingMissingProduct: boolean;
  selectedAddProductOccupantKeys: string[];
  hasAddProductOccupantSelectionChanged: boolean;
  closeAddProductPrompt: () => void;
  toggleAddProductOccupantSelection: (key: string) => void;
  handleConfirmAddProductForOrigin: () => Promise<void>;
}

const occupantHasProduct = (occupant: IOccupant, product: string) => {
  return occupant.products.some(
    (entry) => entry.product.toUpperCase() === product,
  );
};

export const useAddOriginProductManager = ({
  product,
  selectedMonths,
  currentMonth,
  newlyAddedOrigins,
  onOriginRowsAdded,
  setDeletedRowKeysByProduct,
  setNewlyAddedOriginsByProduct,
  setEditingNewlyAddedIds,
  setJustAddedByProduct,
  setAddOriginQuery,
  setOriginTypeFilter,
  setIsAddOriginOpen,
  setFavoriteOriginOptions,
  savedNotification,
  failedNotification,
}: UseAddOriginProductManagerParams) => {
  const [isAddingMissingProduct, setIsAddingMissingProduct] = useState(false);
  const [pendingAddProductPrompt, setPendingAddProductPrompt] =
    useState<PendingAddProductPrompt | null>(null);
  const [selectedAddProductOccupantKeys, setSelectedAddProductOccupantKeys] =
    useState<string[]>([]);
  const [initialAddProductOccupantKeys, setInitialAddProductOccupantKeys] =
    useState<string[]>([]);

  const hasAddProductOccupantSelectionChanged =
    selectedAddProductOccupantKeys.length !==
      initialAddProductOccupantKeys.length ||
    selectedAddProductOccupantKeys.some(
      (key) => !initialAddProductOccupantKeys.includes(key),
    );

  const closeAddProductPrompt = () => {
    setPendingAddProductPrompt(null);
    setSelectedAddProductOccupantKeys([]);
    setInitialAddProductOccupantKeys([]);
  };

  const toggleAddProductOccupantSelection = (key: string) => {
    setSelectedAddProductOccupantKeys((prev) => {
      if (prev.includes(key)) {
        return prev.filter((item) => item !== key);
      }
      return [...prev, key];
    });
  };

  const addOriginRowsToManager = async (option: AddOriginSearchOption) => {
    const { canAddKoch, canAddCompetitor } = option;
    if (!canAddKoch && !canAddCompetitor) {
      return;
    }

    const normalizeOutboundModes = (rawModes: string[]) =>
      rawModes
        .map((mode) => mode.trim().toUpperCase())
        .filter((mode) => mode === "TRUCK" || mode === "RAIL");

    let fetchedDetailModes: string[] | null = null;
    const resolveOutboundModes = async () => {
      if (fetchedDetailModes === null) {
        try {
          const details = await NitroService.getMultiLocationDetails2({
            ids: [option.id],
          });
          const firstDetail = details?.[0];
          let detailModes: string[] = [];
          if (firstDetail) {
            detailModes = firstDetail.outboundModes;
          }
          fetchedDetailModes = normalizeOutboundModes(detailModes);
        } catch {
          fetchedDetailModes = [];
        }
      }

      return fetchedDetailModes;
    };

    const rowsToAdd: IFavoriteLocationExtension[] = [];

    setDeletedRowKeysByProduct((prev) => {
      const next: Record<string, Set<string>> = {
        ...prev,
      };
      const setForProduct = new Set(next[product] ?? new Set<string>());
      if (canAddKoch) {
        setForProduct.delete(`koch-${option.id}`);
      }
      if (canAddCompetitor) {
        setForProduct.delete(`competitor-${option.id}`);
      }
      next[product] = setForProduct;
      return next;
    });

    const currentYear = new Date().getFullYear();

    const buildNewRow = async (
      competitor: boolean,
    ): Promise<IFavoriteLocationExtension> => {
      const outboundModes = await resolveOutboundModes();
      return {
        order: 0,
        id: "",
        location: {
          id: option.id,
          name: option.name,
          outboundModes,
        },
        competitor,
        months: selectedMonths.map((month) => {
          const monthIndex = MONTHS_LOCAL.indexOf(month);
          const monthNum = monthIndex + 1;
          const year =
            monthIndex >= currentMonth ? currentYear : currentYear + 1;
          const lastDay = new Date(year, monthNum, 0).getDate();
          const firstPeriod = {
            id: "",
            infos: [],
            startDate: `${year}-${monthNum.toString().padStart(2, "0")}-01`,
            endDate: `${year}-${monthNum
              .toString()
              .padStart(2, "0")}-${lastDay.toString()}`,
            posted: {
              id: "",
              price: null,
              showAs: "PRICE" as const,
              updatedBy: "",
              updatedAt: "",
            },
            truck: {
              id: "",
              price: null,
              showAs: "PRICE" as const,
              updatedBy: "",
              updatedAt: "",
            },
            rail: {
              id: "",
              price: null,
              showAs: "PRICE" as const,
              updatedBy: "",
              updatedAt: "",
            },
          };
          return {
            month: `${year}-${monthNum.toString().padStart(2, "0")}-01`,
            parsedMonth: { year, month: monthNum, day: 1 },
            periods: [firstPeriod],
            split: false,
            id: "",
            firstPeriod,
            secondPeriod: null,
          };
        }),
      };
    };

    if (canAddKoch) {
      rowsToAdd.push(await buildNewRow(false));
    }
    if (canAddCompetitor) {
      rowsToAdd.push(await buildNewRow(true));
    }

    if (rowsToAdd.length === 0) {
      return;
    }

    const newlyAdded = [...rowsToAdd, ...(newlyAddedOrigins[product] ?? [])];

    setNewlyAddedOriginsByProduct((prev) => ({
      ...prev,
      [product]: newlyAdded,
    }));

    setEditingNewlyAddedIds((prev) => {
      const next = new Set(prev);
      rowsToAdd.forEach((row) => next.add(row.location.id));
      return next;
    });

    setJustAddedByProduct((prev) => ({
      ...prev,
      [product]: true,
    }));

    setAddOriginQuery("");
    setOriginTypeFilter("all");
    setIsAddOriginOpen(false);

    if (onOriginRowsAdded) {
      await onOriginRowsAdded(rowsToAdd);
    }
  };

  const handleConfirmAddProductForOrigin = async () => {
    if (!pendingAddProductPrompt) {
      return;
    }
    const { option, location } = pendingAddProductPrompt;

    setIsAddingMissingProduct(true);
    try {
      // Single-occupant origins don't show the checklist, so the button click itself is the "add" intent.
      const hasMultipleOccupants = location.occupants.length > 1;
      const occupantsToAdd = hasMultipleOccupants
        ? location.occupants.filter(
            (occupant) =>
              selectedAddProductOccupantKeys.includes(occupant.id) &&
              !occupantHasProduct(occupant, product),
          )
        : location.occupants.filter(
            (occupant) => !occupantHasProduct(occupant, product),
          );
      const occupantsToRemove = hasMultipleOccupants
        ? location.occupants.filter(
            (occupant) =>
              !selectedAddProductOccupantKeys.includes(occupant.id) &&
              occupantHasProduct(occupant, product),
          )
        : [];

      let didAssociateProduct = false;
      let didDisassociateProduct = false;
      // Reflects the occupant/product state after this confirmation, used below to decide which sides to add.
      const updatedOccupants = location.occupants.map((occupant) => {
        if (occupantsToAdd.some((added) => added.id === occupant.id)) {
          didAssociateProduct = true;
          return {
            ...occupant,
            products: [...occupant.products, { product, quantity: 0 }],
          };
        }

        if (occupantsToRemove.some((removed) => removed.id === occupant.id)) {
          didDisassociateProduct = true;
          return {
            ...occupant,
            products: occupant.products.filter(
              (entry) => entry.product.toUpperCase() !== product,
            ),
          };
        }

        return occupant;
      });
      if (didAssociateProduct || didDisassociateProduct) {
        await NitroService.updateOccupants({
          id: option.id,
          occupants: updatedOccupants,
        });

        // Update the favorite origin options to reflect the current state of the product association.
        setFavoriteOriginOptions((prev) =>
          prev.map((favoriteOriginOption) => {
            // Skip updating this favorite origin option if it doesn't match the current option OR if it already includes the product.
            if (
              favoriteOriginOption.id !== option.id ||
              favoriteOriginOption.products.includes(product)
            ) {
              return favoriteOriginOption;
            }

            return {
              ...favoriteOriginOption,
              products: Array.from(
                new Set([...favoriteOriginOption.products, product]),
              ),
            };
          }),
        );
      }

      // Only add a side's row if an occupant on that side actually carries the product now.
      const kochHasProduct = updatedOccupants.some(
        (occupant) =>
          occupant.types.includes("Koch") &&
          occupantHasProduct(occupant, product),
      );
      const competitorHasProduct = updatedOccupants.some(
        (occupant) =>
          occupant.types.includes("Competitor") &&
          occupantHasProduct(occupant, product),
      );
      await addOriginRowsToManager({
        ...option,
        canAddKoch: option.canAddKoch && kochHasProduct,
        canAddCompetitor: option.canAddCompetitor && competitorHasProduct,
      });

      if (didAssociateProduct && didDisassociateProduct) {
        savedNotification(`Updated ${product} occupants for ${option.name}.`);
      } else if (didAssociateProduct) {
        savedNotification(`Added ${product} to ${option.name}.`);
      } else if (didDisassociateProduct) {
        savedNotification(`Removed ${product} from ${option.name}.`);
      }
    } catch (error) {
      console.error(
        "Failed to add missing product before adding origin.",
        error,
      );
      failedNotification(
        "Failed to add product",
        "We could not associate this product with the selected origin.",
      );
    } finally {
      setIsAddingMissingProduct(false);
      closeAddProductPrompt();
    }
  };

  const handleAddOriginOptionClick = async (option: AddOriginSearchOption) => {
    const { canAddKoch, canAddCompetitor } = option;

    try {
      // Fetch the latest location details to determine if the product needs to be added to any occupants.
      const location = await NitroService.getLocationDetails2(option.id);
      if (!location) throw new Error("Location not found");

      // If there are any occupants, determine if the product needs to be added to any of them.
      if (location.occupants.length > 0) {
        const kochOccupants = location.occupants.filter((o) =>
          o.types.includes("Koch"),
        );
        const competitorOccupants = location.occupants.filter((o) =>
          o.types.includes("Competitor"),
        );
        // could have customer occupants which we are not interested in here
        const hasKochOrCompetitorOccupants =
          kochOccupants.length > 0 || competitorOccupants.length > 0;

        let needsProductModal: boolean;
        if (hasKochOrCompetitorOccupants) {
          // A side needs review if any of its occupants lack the product.
          const kochSideNeedsProduct =
            canAddKoch &&
            kochOccupants.length > 0 &&
            kochOccupants.some((o) => !occupantHasProduct(o, product));
          const competitorSideNeedsProduct =
            canAddCompetitor &&
            competitorOccupants.length > 0 &&
            competitorOccupants.some((o) => !occupantHasProduct(o, product));
          needsProductModal =
            kochSideNeedsProduct || competitorSideNeedsProduct;
        } else {
          needsProductModal = location.occupants.some(
            (occupant) => !occupantHasProduct(occupant, product),
          );
        }

        if (needsProductModal) {
          setPendingAddProductPrompt({
            option,
            product,
            location,
          });
          const initialSelectedOccupantKeys = location.occupants
            .filter((occupant) => occupantHasProduct(occupant, product))
            .map((occupant) => occupant.id);
          setSelectedAddProductOccupantKeys(initialSelectedOccupantKeys);
          setInitialAddProductOccupantKeys(initialSelectedOccupantKeys);
          setIsAddOriginOpen(false);
          return;
        }
      }
    } catch (error) {
      console.error(
        "Failed to check origin occupants before adding origin.",
        error,
      );
      failedNotification(
        "Failed to fetch origin occupants",
        "We could not retrieve occupants for this origin at this time.",
      );
      setSelectedAddProductOccupantKeys([]);
      setInitialAddProductOccupantKeys([]);
      setIsAddOriginOpen(false);
      return;
    }

    // TODO: this appears when a user tries to add an origin but it is already added...
    if (!canAddKoch && !canAddCompetitor) {
      failedNotification(
        "Origin already added",
        `${option.name} already has ${product} for all occupants.`,
      );
      return;
    }

    await addOriginRowsToManager(option);
  };

  return {
    handleAddOriginOptionClick,
    pendingAddProductPrompt,
    isAddingMissingProduct,
    selectedAddProductOccupantKeys,
    hasAddProductOccupantSelectionChanged,
    closeAddProductPrompt,
    toggleAddProductOccupantSelection,
    handleConfirmAddProductForOrigin,
  };
};

export const AddProductPromptModal: React.FC<AddProductPromptModalProps> = (
  props: AddProductPromptModalProps,
) => {
  const {
    pendingAddProductPrompt,
    isAddingMissingProduct,
    selectedAddProductOccupantKeys,
    hasAddProductOccupantSelectionChanged,
    closeAddProductPrompt,
    toggleAddProductOccupantSelection,
    handleConfirmAddProductForOrigin,
  } = props;

  if (!pendingAddProductPrompt) {
    return null;
  }

  const showChecklist = pendingAddProductPrompt.location.occupants.length > 1;

  let confirmButtonLabel = "Continue";
  if (isAddingMissingProduct) {
    confirmButtonLabel = "Adding...";
  } else if (hasAddProductOccupantSelectionChanged) {
    confirmButtonLabel = "Update Occupants";
  }

  return createPortal(
    <div
      className="dom-save-discard-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Add Product To Origin"
    >
      <div
        className={
          showChecklist
            ? "dom-add-product-occupant-modal"
            : "dom-add-product-confirm-modal"
        }
      >
        <div className="dom-add-product-confirm-header">
          <h3 className="dom-add-product-confirm-title">
            Review {pendingAddProductPrompt.product} Occupants at this origin
          </h3>
          <button
            type="button"
            className="dom-add-product-confirm-close-btn"
            aria-label="Close"
            onClick={closeAddProductPrompt}
            disabled={isAddingMissingProduct}
          >
            <Close fontSize="small" />
          </button>
        </div>
        {showChecklist ? (
          <>
            <p className="dom-add-product-occupant-message">
              Review which occupants store {pendingAddProductPrompt.product} at
              this origin. Make changes if needed, or continue.
            </p>

            <div className="dom-add-product-occupant-list">
              {pendingAddProductPrompt.location.occupants.map((occupant) => {
                const isSelected = selectedAddProductOccupantKeys.includes(
                  occupant.id,
                );

                return (
                  <button
                    key={occupant.id}
                    type="button"
                    className={`dom-add-product-occupant-item ${
                      isSelected ? "dom-add-product-occupant-item-selected" : ""
                    }`}
                    onClick={() =>
                      toggleAddProductOccupantSelection(occupant.id)
                    }
                    disabled={isAddingMissingProduct}
                  >
                    <span
                      className={`dom-add-product-occupant-checkbox ${
                        isSelected
                          ? "dom-add-product-occupant-checkbox-selected"
                          : ""
                      }`}
                      aria-hidden="true"
                    >
                      {isSelected ? "✓" : ""}
                    </span>
                    <span className="dom-add-product-occupant-label">
                      {occupant.name}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="dom-add-product-confirm-actions">
              <button
                type="button"
                className="dom-add-product-confirm-btn"
                onClick={() => {
                  handleConfirmAddProductForOrigin().catch(() => {});
                }}
                disabled={
                  isAddingMissingProduct ||
                  selectedAddProductOccupantKeys.length === 0
                }
              >
                {confirmButtonLabel}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="dom-add-product-confirm-message">
              {pendingAddProductPrompt.product} is not currently associated with
              this origin. Would you like to add it?
            </p>
            <div className="dom-add-product-confirm-actions">
              <button
                type="button"
                className="dom-add-product-confirm-btn"
                onClick={() => {
                  handleConfirmAddProductForOrigin().catch(() => {});
                }}
                disabled={isAddingMissingProduct}
              >
                {isAddingMissingProduct ? "Adding..." : "Add Product"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
};
